/**
 * RegionInfoCard — карточка показателей территории (redesign по макету
 * «модалка региона»): заголовок, таблица показателей в рамке, кнопки
 * Паспорт / Аналитика / Проекты. Используется в двух местах:
 *  - leaflet-попап маркера СНП (клик по маркеру);
 *  - оверлей поверх карты при выборе КАТО в селекторе (с кнопкой закрытия).
 *
 * Данные тянет сам при монтировании — GET /api/public/general?kato=…
 */
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { cn } from '../../lib/cn';
import { getGeneral } from '../../requests/public.request';
import type { GeneralInfo } from '../../types/public';
import { Skeleton } from '../ui/skeleton';

interface RegionInfoCardProps {
  kato: number;
  name: string;
  /** Есть onClose → рисуем крестик (оверлей); в leaflet-попапе свой. */
  onClose?: () => void;
  className?: string;
}

const fmt = (v: number): string => Intl.NumberFormat('ru-RU').format(v);

const actionButton =
  'flex h-10 flex-1 items-center justify-center rounded-lg bg-gradient-to-b from-[#49C24D] to-[#2E9E45] px-3 text-sm font-bold text-white shadow-[0_2px_6px_rgba(46,158,69,0.4)] transition-opacity hover:opacity-90';

export const RegionInfoCard = ({
  kato,
  name,
  onClose,
  className,
}: RegionInfoCardProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [general, setGeneral] = useState<GeneralInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getGeneral(kato)
      .then((response) => {
        if (!cancelled) setGeneral(response.general);
      })
      .catch((err) => {
        if (!cancelled) console.error('RegionInfoCard getGeneral failed', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [kato]);

  const rows: Array<{
    key: string;
    label: string;
    unit: string;
    value: number | null;
    danger?: boolean;
  }> = general
    ? [
        {
          key: 'population',
          label: t('menu.population'),
          unit: t('units.people'),
          value: general.population,
        },
        {
          key: 'village_count',
          label: t('menu.snpCount'),
          unit: t('units.snp'),
          value: general.village_count,
        },
        {
          key: 'saldo_migration',
          label: t('menu.saldoMigration'),
          unit:
            general.saldo_migration != null && general.saldo_migration < 0
              ? t('units.outflow')
              : t('units.inflow'),
          value: general.saldo_migration,
          danger:
            general.saldo_migration != null && general.saldo_migration < 0,
        },
        {
          key: 'avg_salary',
          label: t('menu.avgSalary'),
          unit: t('units.tenge'),
          value: general.avg_salary,
        },
        {
          key: 'unemployed',
          label: t('menu.unemployed'),
          unit: t('units.people'),
          value: general.unemployed,
        },
        {
          key: 'area_km2',
          label: t('menu.area'),
          unit: t('units.km2'),
          value: general.area_km2,
        },
      ]
    : [];

  return (
    <div
      className={cn(
        'w-[340px] max-w-[calc(100vw-2rem)] rounded-[20px] bg-white p-5 font-sans shadow-[0_16px_48px_rgba(0,0,0,0.22)]',
        className
      )}
      data-testid="region-info-card"
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-[17px] font-bold leading-snug text-text">
          {name}
        </h3>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="shrink-0 rounded-md p-1 text-text-muted transition-colors hover:bg-surface-muted hover:text-text"
          >
            <X className="size-5" />
          </button>
        )}
      </div>

      {loading && (
        <div
          className="mt-3 flex flex-col gap-2"
          data-testid="region-info-loading"
        >
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-2/3" />
        </div>
      )}

      {!loading && general && (
        <div className="mt-3 flex flex-col divide-y divide-surface-border overflow-hidden rounded-xl border border-surface-border">
          {rows
            .filter((row) => row.value !== null)
            .map((row) => (
              <div
                key={row.key}
                className="flex items-center justify-between gap-2 px-3.5 py-2.5 text-[13px]"
              >
                <span className="font-medium text-text">{row.label}</span>
                <span className="flex items-baseline gap-1.5">
                  <span className="text-text-faint">{row.unit}</span>
                  <span
                    className={cn(
                      'font-bold tabular-nums',
                      row.danger ? 'text-status-danger' : 'text-[#3AA655]'
                    )}
                  >
                    {fmt(row.value as number)}
                  </span>
                </span>
              </div>
            ))}
        </div>
      )}

      {!loading && !general && (
        <p className="mt-3 text-[13px] text-text-muted">{t('common.noData')}</p>
      )}

      <div className="mt-4 flex gap-2.5">
        <button
          type="button"
          className={actionButton}
          onClick={() => navigate(`/${kato}/digital-passport`)}
        >
          {t('mapPopup.passport')}
        </button>
        <button
          type="button"
          className={actionButton}
          onClick={() => navigate('/analytics')}
        >
          {t('mapPopup.analytics')}
        </button>
        <button
          type="button"
          disabled
          className={cn(actionButton, 'cursor-not-allowed opacity-60')}
        >
          {t('mapPopup.projects')}
        </button>
      </div>
    </div>
  );
};
